import React, {useState, useEffect, useMemo} from 'react';
import {useRouter} from 'next/router';
import {RadioGroup} from '@headlessui/react';
import {compact, uniq, keyBy} from 'lodash';
import cx from 'classnames';

import {
  EyeIcon,
  UserGroupIcon,
  DatabaseIcon,
  DocumentIcon,
  QuestionMarkCircleIcon,
  ChatIcon,
} from '@heroicons/react/outline';
import Link from '../../components/Link';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import PlanCard from '../../components/PlanCard';
import {TestimonialSection} from '../../components/TestimonialSection';
import Testimonial from '../../components/TestimonialCard';
import DocumentHeader from '../../components/DocumentHeader';
import StickyHeader from '../../components/StickyHeader';
import FAQs from './UpgradeFAQs';
import {startPreCheckout} from '../PreCheckoutPage';
import {freeFeatures, membershipFeatures} from '../../constants/product';

import {getMonthlyPriceStr} from '../../util/index';
import {useAppContext, useAnalytics} from '../../hooks';

const featureList = [
  {
    title: 'Understand what interviewers want',
    text: 'Our courses, built from insights from 2,000+ interviews, provide everything you need to succeed—interview rubrics, real-world examples, essential concepts, and expert tips from top tech companies.',
    icon: EyeIcon,
  },
  {
    title: 'Essential interview questions',
    text: "Refine your skills with peer feedback, practice problems, and step-by-step interactive lessons for tech's most popular interview loops and challenges.",
    icon: QuestionMarkCircleIcon,
  },
  {
    title: 'Grow your network',
    text: 'Join our exclusive Slack community of professionals to share advice, gain insights, and build relationships that open doors.',
    icon: UserGroupIcon,
  },
  {
    title: 'Inside scoop',
    text: 'Access our database of thousands of verified interview questions, updated in real-time by candidates. Watch 350+ hours of expert-led video answers to see what works—and what doesn’t.',
    icon: DatabaseIcon,
  },
  {
    title: 'Practice with peers',
    text: 'Practice makes perfect. Build confidence and receive personalized feedback during daily mock interview sessions with peers and industry experts.',
    icon: ChatIcon,
  },
  {
    title: 'Get referred',
    text: 'Our membership connects you with referrers at top companies. Get your resume into the right hands.',
    icon: DocumentIcon,
  },
];

const frequencies = [
  {value: 'monthly', label: 'Monthly', priceSuffix: '/month'},
  {value: 'annually', label: 'Annually', priceSuffix: '/year'},
];

const roleToTestimonials = {
  // Role
  pm: ['4NX4AjS2YhD6N8ZPl3HsON', '4GnUu7Xb6qq2v7V6UB6QzT'],
  swe: ['7j0TkJzk0LPXeKsMjfHFkj', '2hXOLMI0L9C4EfqDaE74rB'],
  em: ['3f0tLVNbHncjTAwWgTyRZ8', '3ipFlTeHfa31syDId3Z0mS'],
  tpm: ['1vejwFArlHDuEB4cF5lWti', '6hiYPlrhpXHVNshXP09eaQ'],
  'ml-engineer': ['2mTUlaVeLZ4ZYYOJD5J8BP'], // TODO: 1 more testimonial
  'data-science': ['rDulRa1V4Q9nN7F8QRzjM', '2tqb1P7usFB8HZy59GFqJb'],
  // TODO: data-engineer, data-analyst, solutions-architect, bizops

  // Role category
  engineering: ['2hXOLMI0L9C4EfqDaE74rB', '1ucRaki38UnSLgqCgLuaVf'],
  marketing: ['i2dcz9LfM1aOZ6pl3UPwJ'], // TODO: 1 more testimonial
  operations: ['6QhS2h57smvDHGVLn5abjH', '1vMoL7Qzx8OucNZKt3ol4H'],
  design: ['10eJkAbZjxQUsVOLgMJC9J', 'mtlspHUWaEcCcQs4gwRjF'],
  data: ['rDulRa1V4Q9nN7F8QRzjM', '2tqb1P7usFB8HZy59GFqJb'],
  consulting: ['61iC9FMMDbyt8riFI2BXRZ', '26IJQeaAOJicJHMiU8xSec'],
  // TODO: sales, finance
};

const UpgradePage = () => {
  const {
    loading,
    catalog,
    currency,
    onCheckout,
    currentUser,
    hasSubscription,
    features,
    testimonials,
  } = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [course, setCourse] = useState(null);
  const [frequency, setFrequency] = useState(frequencies[1]); // show annual plans

  useEffect(() => {
    if (loading || !router.isReady) return;

    const {query: {c}} = router;
    setCourse(c);
    tracker.upgradeView({type: course});
  }, [loading, router.isReady]);

  useEffect(() => {
    if (hasSubscription) {
      router.push('/account');
    }
  }, [hasSubscription, router]);

  const testimonialMap = useMemo(() => keyBy(testimonials, 'id'), [testimonials]);
  const featuredTestimonials = useMemo(() => {
    const defaultTestimonialIds = ['1w1S8ALR2IX1yqo1ipxOvl', '1ucRaki38UnSLgqCgLuaVf'];
    const [role, roleCategory] = currentUser?.profile?.career_tracks || [undefined, undefined];
    const roleTestimonialsIds = roleToTestimonials[role] || [];
    const roleCategoryTestimonialsIds = roleToTestimonials[roleCategory] || [];

    const combinedIds = uniq([
      ...roleTestimonialsIds, ...roleCategoryTestimonialsIds, ...defaultTestimonialIds,
    ]);

    const filteredTestimonials = compact(combinedIds.map((id) => testimonialMap[id]));

    return filteredTestimonials.slice(0, 2);
  }, [currentUser, testimonialMap]);

  if (!catalog) return null;

  const {
    MEMBERSHIP_MONTHLY,
    MEMBERSHIP_ANNUAL,
  } = catalog;

  const monthlyPlan = MEMBERSHIP_MONTHLY;
  const annualPlan = MEMBERSHIP_ANNUAL;

  const navigationTabs = compact([
    {key: 'plans', label: 'Plans'},
    {key: 'features', label: 'Features'},
    {key: 'reviews', label: 'Reviews'},
    {key: 'faq', label: 'FAQ'},
  ]);

  const handleFree = () => {
    router.push('signup?src=upgrade');
  };

  const handlePayment = () => {
    setPaymentOpen(true);
    const product = frequency === frequencies[1] ? annualPlan : monthlyPlan;
    const cart = [{id: product.name, quantity: 1}];

    if (features?.enable_pre_checkout === 'on') {
      startPreCheckout(router, {cart, tag: course}, tracker);
    } else {
      onCheckout(
        cart,
        currentUser,
        {tag: course, currency: currency.code},
        () => setPaymentOpen(false),
      );
    }
    setPaymentOpen(false);
  };

  const renderPlanCard = () => {
    if (frequency === frequencies[1]) {
      return (
        <>
          <PlanCard
            name="Free"
            description="Get a taste of what Exponent has to offer"
            features={freeFeatures}
            price="$0"
            priceSecondaryText="/ month"
            cta={currentUser ? 'Current plan' : 'Start now'}
            disabled={currentUser}
            onClick={handleFree}
            className="mb-4"
          />
          <PlanCard
            name="Annual"
            description="Access courses, mocks, videos, private community, and AI features"
            highlighted
            features={membershipFeatures}
            badge={{
              text: annualPlan.discountedPriceString ? 'Discounted' : 'Save 70%',
              backgroundColor: '#D8FEE9',
              textColor: '#21A35D',
            }}
            specialFeatures={[annualPlan.specialFeaturesString]}
            price={monthlyPlan.priceString}
            discountedPrice={getMonthlyPriceStr(currency.symbol, annualPlan.price)}
            priceSecondaryText="/ month"
            cta="Start now"
            onClick={handlePayment}
            disabled={paymentOpen}
            className="mb-4"
          />
        </>
      );
    }
    return (
      <>
        <PlanCard
          name="Free"
          description="Get a taste of what Exponent has to offer"
          features={freeFeatures}
          price="$0"
          priceSecondaryText="/ month"
          cta={currentUser ? 'Current plan' : 'Start now'}
          onClick={handleFree}
          disabled={currentUser}
          className="mb-4"
        />
        <PlanCard
          name="Monthly"
          highlighted
          description="Access courses, mocks, videos, private community, and AI features"
          features={membershipFeatures}
          badge={{
            text: monthlyPlan.discountedPriceString ? 'Discounted' : 'Crunch Time',
            backgroundColor: monthlyPlan.discountedPriceString ? '#D8FEE9' : '#D8F7FE',
            textColor: monthlyPlan.discountedPriceString ? '#21A35D' : '#2174A3',
          }}
          specialFeatures={[monthlyPlan.specialFeaturesString]}
          price={monthlyPlan.priceString}
          discountedPrice={monthlyPlan.discountedPriceString}
          priceSecondaryText="/ month"
          cta="Start now"
          onClick={handlePayment}
          disabled={paymentOpen}
          className="mb-4"
        />
      </>
    );
  };

  return (
    <div className="UpgradePage bg-white">
      <DocumentHeader title="Membership and Pricing" />

      {/* Sticky header */}
      <StickyHeader
        navigationTabs={navigationTabs}
        cta="Upgrade now"
        handleClick={handlePayment}
      />

      {/* Title & Description */}
      <div className="mx-auto max-w-7xl px-2 md:px-6" id="plans">
        <div className="mx-auto max-w-4xl text-center pt-12">
          <p className="text-base/7 font-semibold text-indigo-600">Pricing</p>
          <h1 className="mt-2 text-balance text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Unlock the full member experience
          </h1>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-pretty text-center text-lg font-medium text-gray-600 sm:text-xl">
          Get access to all courses, video answers, peer mock interviews,
          private community, AI features, and more.
        </p>
      </div>

      {/* Plans */}
      <div className="mx-auto max-w-7xl px-2 md:px-6 mt-10">
        <div className="flex justify-center">
          <fieldset aria-label="Payment frequency">
            <RadioGroup
              value={frequency}
              onChange={setFrequency}
              className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold ring-1 ring-inset ring-gray-200"
            >
              {frequencies.map((option) => (
                <RadioGroup.Option
                  key={option.value}
                  value={option}
                  className={cx(
                    'flex items-center justify-center rounded-full px-2.5 py-1 cursor-pointer',
                    {'bg-indigo-600': frequency === option},
                    {'bg-white': frequency !== option},
                  )}
                >
                  <span className={frequency === option ? 'text-white' : 'text-gray-500'}>{option.label}</span>
                </RadioGroup.Option>
              ))}
            </RadioGroup>
          </fieldset>
        </div>

        <div className="flex flex-col-reverse md:flex-row gap-x-6 justify-center items-center md:items-stretch mt-10">
          {renderPlanCard()}
        </div>
      </div>

      {/* Expert coaching */}
      <div className="mx-auto max-w-7xl px-2 md:px-6 mt-4 text-center font-medium text-base">
        <p>
          Want expert coaching?
          {' '}
          <Link
            href="/coaching/start?src=upgrade"
            target="_blank"
            className="font-medium text-indigo ml-1 appearance-none"
          >
            Add coaching to your membership
          </Link>
        </p>
      </div>

      {/* Product marketing */}
      <div className="max-w-7xl mx-auto px-6 mt-16" id="features">
        <h2 className="text-xl md:text-3xl text-center font-bold text-black">Land the job with Exponent</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16 mt-12">
          {featureList.map((feature) => (
            <div className="flex flex-col text-lg">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-indigo text-white">
                <feature.icon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-gray-900 mt-5">{feature.title}</h3>
              <p className="mt-2 text-gray-600">{feature.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-16" id="reviews">
        <h2 className="text-xl md:text-3xl text-center font-bold text-black">Our 600,000+ happy users</h2>
        <p className="text-center text-base text-gray-500 mt-1">Still not sure? Check out what our members are saying about us</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {featuredTestimonials.map((testimonial) => (
            <Testimonial testimonial={testimonial} key={testimonial.id} />
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-2 md:px-6 mt-8 text-center font-medium text-base">
        <Link
          href="/reviews?src=upgrade"
          target="_blank"
          className="font-medium text-indigo appearance-none"
        >
          See reviews from other roles
        </Link>
      </div>

      {/* FAQ */}
      <div className="mt-16 text-center justify-center" id="faq">
        <h2 className="mb-2 text-xl md:text-3xl text-center font-bold text-black">Frequently asked questions</h2>
        <FAQs includeRefundPolicy />
      </div>

      <TestimonialSection />
      <div className="flex flex-col items-center text-center pb-12">
        <span className="mb-4 text-xl font-medium text-gray-500">Ready to join the community?</span>
        <Button
          className="px-12"
          size={ButtonSizes.LARGE}
          onClick={handlePayment}
        >
          Start your membership
        </Button>
      </div>
    </div>
  );
};

export default UpgradePage;
